<template>
  <o-dialog
    ref="dialog"
    :title="$t('general.image')"
    :info="$t('components.service.actions.image.info', { title: service.title })"
    :options="[
      { text: $t('general.cancel'), type: 'cancel'},
      { text: $t('asset.deleteConfirm2'), type: 'confirm', variant: 'danger', callback: handleDelete, validate: false },
      { text: $t('asset.upload'), type: 'confirm', callback: handleConfirm },
    ]"
    :portal="portal"
    :valid="validate"
  >
    <div class="mt-4 text-left ">
      <o-form v-if="!loading" ref="form">
        <o-field-group :label="$t('general.image')" :name="$t('general.image')" class="block pb-3">
          <o-upload v-model="image" />
        </o-field-group>
      </o-form>
    </div>

    <template #loading>
      <div v-if="loading" class="w-full max-w-sm mx-auto">
        <o-progress v-if="progress" :progress="progress" :label="$t('general.uploading')" />
        <o-loader v-else class="mx-auto" />
      </div>
    </template>
  </o-dialog>
</template>

<script setup lang="ts">
import { OUpload, OProgress } from '@/components/elements'

import type { Service } from '~/_api/models/service'

const props = defineProps<{
  service: Service
  portal?: string
}>()

const emit = defineEmits<{
  (e: 'success'): void
  (e: 'update', service: Service): void
}>()

const { $api, $notification, $i18n } = useNuxtApp()
const $t = $i18n.t.bind($i18n)

// Template refs
const dialog = ref()
const form = ref()

// State
const image = ref<File | null>(null)
const loading = ref(false)
const progress = ref(0)

const upload = computed({
  get: () => {
    if (image.value instanceof File) {
      return image.value
    }
    return null
  },
  set: (value) => {
    image.value = value
  },
})

const open = () => {
  image.value = null
  dialog.value?.open()
}

const validate = () => {
  return !!image.value
}

const handleOnUploadProgress = (event: ProgressEvent) => {
  progress.value = Math.round((event.loaded / event.total) * 100)
}

const handleConfirm = async () => {
  loading.value = true

  try {
    const imageFile = upload.value
    const options = { onUploadProgress: (event: ProgressEvent) => handleOnUploadProgress(event) }

    const response = await $api.service.addPhoto(props.service, imageFile!, options)
    const service = response.data

    if (service) {
      $notification.toast({
        title: $t('asset.addedPhoto'),
        text: $t('asset.successInfo2', { label: props.service.title }),
        variant: 'success',
      })

      emit('success')
      emit('update', service)

      loading.value = false
      progress.value = 0

      return true
    }
  } catch (e) {
    console.log(e)
    image.value = null
  }

  loading.value = false
}

const handleDelete = async () => {
  loading.value = true

  try {
    const response = await $api.service.deletePhoto(props.service)
    const service = response.data

    if (service) {
      $notification.toast({
        title: $t('asset.deleteSuccess'),
        text: $t('asset.deleteSuccessInfo2', { label: props.service.title }),
        variant: 'success',
      })

      emit('success')
      emit('update', service)

      loading.value = false

      return true
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

defineExpose({
  open,
})
</script>
